import React from 'react';
import Box from "@mui/material/Box";
import makeStyles from "@mui/styles/makeStyles";
import { Typography, Divider } from '@mui/material';
import NewTooltip from './NewTooltip';
import { useTheme } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    background: "white",
    minWidth: 100,
    height: 'fit-content',
    display: "flex",
    position: "relative",
    border: `1px solid ${theme.palette.primary['clr-400']}`,
    borderRadius: '8px',
    overflow: 'hidden'
  },
  slider: {
    position: "absolute",
    top: 0,
    bottom: 0,
    background: theme.palette.primary['clr-400'],
    zIndex: 0,
    transition: "400ms ease-in-out",
    borderRadius: '4px',
    margin: theme.spacing(0.5)
  },
  content: {
    flex: 1,
    padding: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    zIndex: 1,
    letterSpacing: 1,
    color: "#02569D",
    cursor: "pointer",
    transition: "400ms ease-in-out",
  },
  tooltipBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '400px',
    padding: '10px',
  },
  divider: {
    alignSelf: 'stretch',
    zIndex: 1,
    borderColor: '#CCD4DE'
  }
}));

function Toggle({
  width,
  height,
  value = 0,
  options = [],
  tooltips = [],
  onChange = () => { },
  ...props
}) {
  const classes = useStyles();
  const theme = useTheme();

  const handleToggle = (index) => () => {
    if (value !== index) {
      onChange(options[index], index);
    }
  };

  const optionCount = options.length;
  const sliderWidth = `${(100 / optionCount) - 1}%`;
  const sliderLeft = `${(100 / optionCount) * value}%`;

  const renderOption = (index) => {
    const option = options[index];
    const tooltipContent = tooltips[index];
    const isActive = value === index;

    // Create a deep copy of the SVG with updated stroke color
    const iconWithUpdatedColor = option?.icon ? 
      React.cloneElement(option.icon, {
        children: React.Children.map(option.icon.props.children, child => 
          React.cloneElement(child, {
            stroke: isActive ? 'white' : '#3C434D'
          })
        )
      }) : null;

    const optionContent = (
      <Box
        className={classes.content}
        onClick={handleToggle(index)}
        style={{ flex: `0 0 ${100 / optionCount}%` }}
      >
        {option?.icon && (
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
            {iconWithUpdatedColor}
          </Box>
        )}
        <Typography
          variant="h6-medium"
          color={isActive ? 'shades.clr-white-900' : 'neutral.clr-700'}
        >
          {option?.label || `Option ${index + 1}`}
        </Typography>
      </Box>
    );

    const content = (
      <React.Fragment key={index}>
        {tooltipContent ? (
          <NewTooltip component={tooltipContent}>
            {optionContent}
          </NewTooltip>
        ) : (
          optionContent
        )}
        {index < optionCount - 1 && (
          <Divider orientation="vertical" className={classes.divider} />
        )}
      </React.Fragment>
    );

    return content;
  };

  return (
    <Box width={width} {...props}>
      <Box className={classes.container} style={{ height }}>
        <Box
          className={classes.slider}
          sx={{
            width: sliderWidth,
            left: sliderLeft,
          }}
        />
        {options.map((_, index) => renderOption(index))}
      </Box>
    </Box>
  );
}

export default Toggle;